import { h } from 'preact';
import { translateError } from '../i18n.js';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { parseServerDate } from '../utils/dates.js';
import AiAssist from '../components/shared/AiAssist.jsx';

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  const diff = Date.now() - parseServerDate(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'zojuist';
  if (mins < 60) return `${mins}m geleden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}u geleden`;
  return parseServerDate(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

function avatarColor(name) {
  const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444'];
  let h = 0; for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffff;
  return colors[h % colors.length];
}

// ── My Balance Tab ────────────────────────────────────────────────────────────

function MyBalanceTab({ currencyName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/coins/me')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div class="text-muted text-center" style="padding:40px">Laden…</div>;
  if (!data) return null;

  const EARN_ITEMS = [
    { icon: '✅', label: 'Taak op tijd afronden' },
    { icon: '🪜', label: 'Processtap voltooien' },
    { icon: '🏁', label: 'Volledig proces afronden' },
    { icon: '🎂', label: 'Verjaardag' },
    { icon: '📋', label: 'Profiel volledig invullen' },
    { icon: '🔄', label: 'Profiel bijwerken (1x/kwartaal)' },
    { icon: '🎓', label: 'Certificaat behaald' },
  ];

  return (
    <div style="display:flex;flex-direction:column;gap:20px">
      {/* Balance hero */}
      <div style="border-radius:16px;background:linear-gradient(135deg,var(--color-primary),#6366f1);padding:28px;text-align:center;color:#fff">
        <div style="font-size:13px;opacity:0.85;margin-bottom:6px">Jouw saldo</div>
        <div style="font-size:52px;font-weight:800;line-height:1;margin-bottom:4px">
          🪙 {data.balance}
        </div>
        <div style="font-size:14px;opacity:0.75">{currencyName}</div>
      </div>

      {/* Recent transactions */}
      <Card title="📋 Recente transacties">
        {data.transactions.length === 0 ? (
          <p class="text-muted text-sm">Nog geen transacties. Rond een taak af om je eerste coins te verdienen!</p>
        ) : (
          <div style="display:flex;flex-direction:column;gap:2px">
            {data.transactions.map(tx => (
              <div key={tx.id} style="display:flex;align-items:center;justify-content:space-between;padding:8px 4px;border-bottom:1px solid var(--color-border)">
                <div style="flex:1;min-width:0">
                  <div style="font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{tx.reason}</div>
                  <div style="font-size:11px;color:var(--color-text-muted)">{timeAgo(tx.created_at)}</div>
                </div>
                <div style={`font-size:15px;font-weight:700;flex-shrink:0;margin-left:12px;color:${tx.amount > 0 ? '#16a34a' : '#dc2626'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* How to earn */}
      <Card title="💡 Zo verdien je coins">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
          {EARN_ITEMS.map(item => (
            <div key={item.label} style="padding:10px 12px;border-radius:8px;border:1px solid var(--color-border);background:var(--color-bg-secondary);display:flex;align-items:center;gap:8px">
              <span style="font-size:18px">{item.icon}</span>
              <span style="font-size:12px;color:var(--color-text-muted)">{item.label}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── Leaderboard Tab ───────────────────────────────────────────────────────────

function LeaderboardTab({ currencyName }) {
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/coins/leaderboard')
      .then(data => setBoard(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div class="text-muted text-center" style="padding:40px">Laden…</div>;

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <Card title={`🏆 Ranglijst — ${currencyName}`}>
      {board.length === 0 ? (
        <p class="text-muted text-sm text-center" style="padding:20px">Nog geen coins verdiend in deze club.</p>
      ) : (
        <div style="display:flex;flex-direction:column;gap:6px">
          {board.map((entry, i) => (
            <div key={entry.user_id} style={`display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:10px;background:${i === 0 ? '#fef9c3' : i === 1 ? 'var(--color-bg-secondary)' : i === 2 ? '#fef3c7' : 'var(--color-bg-secondary)'};border:1px solid ${i < 3 ? '#e5e7eb' : 'var(--color-border)'}`}>
              <div style="font-size:22px;width:28px;text-align:center;flex-shrink:0">
                {medals[i] || <span style="font-size:14px;color:var(--color-text-muted);font-weight:600">{i + 1}</span>}
              </div>
              <div style={`width:36px;height:36px;border-radius:50%;background:${avatarColor(entry.display_name)};display:flex;align-items:center;justify-content:font-size:13px;font-weight:700;color:#fff;flex-shrink:0;align-items:center;justify-content:center`}>
                {(entry.display_name || '?')[0].toUpperCase()}
              </div>
              <div style="flex:1;min-width:0">
                <div style="font-size:13px;font-weight:600">{entry.display_name || 'Onbekend'}</div>
              </div>
              <div style="text-align:right;flex-shrink:0">
                <div style="font-size:16px;font-weight:700;color:var(--color-primary)">🪙 {entry.balance}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Rewards Shop Tab ──────────────────────────────────────────────────────────

function RewardsTab({ currencyName }) {
  const [rewards, setRewards] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    Promise.all([api('/api/coins/rewards'), api('/api/coins/me')])
      .then(([rw, me]) => {
        setRewards(Array.isArray(rw) ? rw : []);
        setBalance(me.balance ?? 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleRedeem(reward) {
    if (!confirm(`Wil je "${reward.name}" inwisselen voor ${reward.cost_coins} coins?`)) return;
    setRedeeming(reward.id);
    setMessage(null);
    try {
      await api('/api/coins/redeem', { method: 'POST', body: JSON.stringify({ rewardId: reward.id }) });
      setBalance(b => b - reward.cost_coins);
      setMessage({ type: 'success', text: `✅ Aanvraag ingediend voor "${reward.name}"! De beheerder beoordeelt je verzoek.` });
    } catch (err) {
      const msgs = { insufficient_coins: 'Je hebt niet genoeg coins.', out_of_stock: 'Deze beloning is niet meer beschikbaar.' };
      setMessage({ type: 'error', text: msgs[err.code] || 'Er ging iets mis.' });
    }
    setRedeeming(null);
  }

  if (loading) return <div class="text-muted text-center" style="padding:40px">Laden…</div>;

  return (
    <div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;padding:10px 14px;background:var(--color-bg-secondary);border-radius:10px;border:1px solid var(--color-border)">
        <span style="font-size:13px;color:var(--color-text-muted)">Jouw saldo</span>
        <span style="font-size:18px;font-weight:700;color:var(--color-primary)">🪙 {balance} {currencyName}</span>
      </div>

      {message && (
        <div class={`alert alert-${message.type === 'success' ? 'success' : 'error'} mb-4`} style="font-size:13px">{message.text}</div>
      )}

      {rewards.length === 0 ? (
        <div class="text-muted text-center" style="padding:40px">Nog geen beloningen aangemaakt door de beheerder.</div>
      ) : (
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px">
          {rewards.map(reward => {
            const canAfford = balance >= reward.cost_coins;
            const outOfStock = reward.stock !== null && reward.stock <= 0;
            return (
              <div key={reward.id} style={`border-radius:12px;border:1px solid var(--color-border);background:var(--color-surface);overflow:hidden;display:flex;flex-direction:column;opacity:${outOfStock ? 0.5 : 1}`}>
                {reward.image_url && (
                  <img src={reward.image_url} alt={reward.name} style="width:100%;height:160px;object-fit:cover;display:block" />
                )}
                <div style="padding:16px;flex:1">
                  <div style="font-size:15px;font-weight:600;margin-bottom:4px">{reward.name}</div>
                  {reward.description && <div style="font-size:12px;color:var(--color-text-muted);margin-bottom:10px;line-height:1.4">{reward.description}</div>}
                  <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">
                    <span style="font-size:16px;font-weight:700;color:var(--color-primary)">🪙 {reward.cost_coins}</span>
                    {reward.stock !== null && (
                      <span style="font-size:11px;color:var(--color-text-muted)">
                        {outOfStock ? '— Uitverkocht' : `— Nog ${reward.stock} beschikbaar`}
                      </span>
                    )}
                  </div>
                </div>
                <div style="padding:10px 16px;border-top:1px solid var(--color-border)">
                  <button
                    onClick={() => handleRedeem(reward)}
                    disabled={!canAfford || outOfStock || redeeming === reward.id}
                    style={`width:100%;padding:7px;border-radius:8px;border:none;font-size:13px;font-weight:500;cursor:${canAfford && !outOfStock ? 'pointer' : 'not-allowed'};background:${canAfford && !outOfStock ? 'var(--color-primary)' : 'var(--color-border)'};color:${canAfford && !outOfStock ? '#fff' : 'var(--color-text-muted)'}`}
                  >
                    {redeeming === reward.id ? '…' : outOfStock ? 'Uitverkocht' : !canAfford ? 'Niet genoeg coins' : 'Inwisselen'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Admin Tab ─────────────────────────────────────────────────────────────────

const ACTION_TYPES = [
  { key: 'task_on_time', coinsKey: 'task_on_time_coins', autoKey: 'task_on_time_auto', label: 'Taak op tijd afgerond' },
  { key: 'step',         coinsKey: 'step_completed_coins',  autoKey: 'step_auto',         label: 'Processtap voltooid' },
  { key: 'process',      coinsKey: 'process_completed_coins', autoKey: 'process_auto',    label: 'Volledig proces afgerond' },
  { key: 'certificate',  coinsKey: 'certificate_coins',    autoKey: null, approval: true, label: 'Certificaat behaald (1x per certificaat)' },
  { key: 'document',     coinsKey: 'document_coins',       autoKey: 'document_auto',      label: 'Document geüpload' },
  { key: 'profile_update', coinsKey: 'profile_update_coins', autoKey: 'profile_update_auto', label: 'Profiel bijgewerkt (1x/kwartaal)' },
  { key: 'profile_complete', coinsKey: 'profile_complete_coins', autoKey: null,            label: 'Profiel volledig ingevuld (eenmalig, altijd auto)' },
  { key: 'birthday',     coinsKey: 'birthday_coins',       autoKey: null,                 label: 'Verjaardag (altijd auto)' },
  { key: 'jaarplanning', coinsKey: 'jaarplanning_coins',   autoKey: null,                 label: 'Jaarplanning taak afgerond (altijd auto)' },
];

function AdminTab() {
  const [settings, setSettings] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [rewardForm, setRewardForm] = useState(null);
  const [savingReward, setSavingReward] = useState(false);
  const [rewardError, setRewardError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [handling, setHandling] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    Promise.all([
      api('/api/coins/settings'),
      api('/api/coins/rewards').catch(() => []),
      api('/api/coins/redemptions').catch(() => []),
      api('/api/coins/pending').catch(() => []),
    ]).then(([s, rw, rd, pend]) => {
      setSettings(s);
      setForm({ ...s });
      setRewards(Array.isArray(rw) ? rw : []);
      setRedemptions(Array.isArray(rd) ? rd : []);
      setPending(Array.isArray(pend) ? pend : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleSaveSettings() {
    setSavingSettings(true);
    try {
      const updated = await api('/api/coins/settings', { method: 'PUT', body: JSON.stringify(form) });
      setSettings(updated);
      setForm({ ...updated });
    } catch {}
    setSavingSettings(false);
  }

  async function handleSaveReward() {
    setSavingReward(true);
    setRewardError(null);
    try {
      if (rewardForm.id) {
        await api(`/api/coins/rewards/${rewardForm.id}`, { method: 'PUT', body: JSON.stringify(rewardForm) });
        setRewards(rw => rw.map(r => r.id === rewardForm.id ? { ...r, ...rewardForm } : r));
        setRewardForm(null);
      } else {
        const created = await api('/api/coins/rewards', { method: 'POST', body: JSON.stringify(rewardForm) });
        setRewards(rw => [...rw, created]);
        // Keep form open in edit mode so admin can immediately add an image
        setRewardForm(created);
      }
    } catch (e) {
      setRewardError(e?.message || 'Opslaan mislukt');
    }
    setSavingReward(false);
  }

  async function handleUploadImage(file) {
    if (!rewardForm?.id || !file) return;
    setUploadingImage(true);
    setRewardError(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(`/api/coins/rewards/${rewardForm.id}/image`, { method: 'POST', body: fd });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.error || 'upload_failed');
      setRewardForm(f => ({ ...f, image_url: result.image_url }));
      setRewards(rw => rw.map(r => r.id === rewardForm.id ? { ...r, image_url: result.image_url } : r));
    } catch {
      setRewardError('Foto uploaden mislukt');
    }
    setUploadingImage(false);
  }

  async function handleDeleteReward(id) {
    if (!confirm('Beloning verwijderen?')) return;
    await api(`/api/coins/rewards/${id}`, { method: 'DELETE' });
    setRewards(rw => rw.filter(r => r.id !== id));
  }

  async function handleRedemption(id, status) {
    setHandling(id + ':redemption');
    try {
      await api(`/api/coins/redemptions/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      setRedemptions(rd => rd.map(r => r.id === id ? { ...r, status } : r));
    } catch {}
    setHandling(null);
  }

  async function handlePending(id, status) {
    setHandling(id + ':pending');
    try {
      await api(`/api/coins/pending/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      setPending(p => p.map(a => a.id === id ? { ...a, status } : a));
    } catch (err) {
      if (err.code === 'own_award') alert(translateError(err.code));
    }
    setHandling(null);
  }

  if (loading) return <div class="text-muted text-center" style="padding:40px">Laden…</div>;

  const setRF = (k, v) => setRewardForm(f => ({ ...f, [k]: v }));
  const pendingRedemptions = redemptions.filter(r => r.status === 'pending');
  const pendingAwards = pending.filter(a => a.status === 'pending');

  return (
    <div style="display:flex;flex-direction:column;gap:20px">

      {/* Pending coin awards */}
      {pendingAwards.length > 0 && (
        <Card title={`⏳ Goed te keuren coins (${pendingAwards.length})`}>
          <div style="display:flex;flex-direction:column;gap:6px">
            {pending.map(a => (
              <div key={a.id} style={`display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:8px;border:1px solid var(--color-border);background:${a.status === 'pending' ? 'var(--color-bg-secondary)' : 'transparent'};opacity:${a.status !== 'pending' ? 0.5 : 1}`}>
                <div style="flex:1;min-width:0">
                  <div style="font-size:13px;font-weight:500">{a.user_name}</div>
                  <div style="font-size:12px;color:var(--color-text-muted)">{a.reason}</div>
                  <div style="font-size:11px;color:var(--color-text-muted)">🪙 +{a.amount} · {timeAgo(a.created_at)}</div>
                </div>
                {a.status === 'pending' ? (
                  <div style="display:flex;gap:4px;flex-shrink:0">
                    <button onClick={() => handlePending(a.id, 'approved')} disabled={handling === a.id + ':pending'}
                      style="background:#16a34a;color:#fff;border:none;border-radius:6px;padding:4px 10px;cursor:pointer;font-size:12px;font-weight:500">
                      ✓ Goedkeuren
                    </button>
                    <button onClick={() => handlePending(a.id, 'rejected')} disabled={handling === a.id + ':pending'}
                      style="background:#dc2626;color:#fff;border:none;border-radius:6px;padding:4px 10px;cursor:pointer;font-size:12px;font-weight:500">
                      ✗ Afwijzen
                    </button>
                  </div>
                ) : (
                  <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${a.status === 'approved' ? '#dcfce7' : '#fee2e2'};color:${a.status === 'approved' ? '#16a34a' : '#dc2626'}`}>
                    {a.status === 'approved' ? 'Goedgekeurd' : 'Afgewezen'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Feature toggle */}
      <Card title="🔌 Coins aan/uit">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px">
          <div>
            <div style="font-size:13px;font-weight:600">{form.coins_enabled ? '✅ Coins is ingeschakeld' : '⏸ Coins is uitgeschakeld'}</div>
            <div style="font-size:12px;color:var(--color-text-muted);margin-top:2px">
              {form.coins_enabled
                ? 'Alle leden zien de Coins-pagina in het menu.'
                : 'De Coins-pagina is verborgen voor alle leden. Alleen jij als beheerder ziet dit scherm.'}
            </div>
          </div>
          <button
            onClick={async () => {
              const next = !form.coins_enabled;
              setForm(f => ({...f, coins_enabled: next}));
              try { await api('/api/coins/settings', { method: 'PUT', body: JSON.stringify({...form, coins_enabled: next}) }); } catch {}
            }}
            style={`padding:7px 16px;border-radius:8px;border:none;font-size:13px;font-weight:600;cursor:pointer;background:${form.coins_enabled ? '#dc2626' : '#16a34a'};color:#fff;flex-shrink:0`}
          >
            {form.coins_enabled ? 'Uitschakelen' : 'Inschakelen'}
          </button>
        </div>
      </Card>

      {/* Coin settings */}
      <Card title="⚙️ Instellingen">
        <div style="display:flex;flex-direction:column;gap:12px">
          <div class="form-group">
            <label class="form-label">Naam van de munt</label>
            <input class="form-input" value={form.currency_name} onInput={e => setForm(f => ({...f, currency_name: e.target.value}))} placeholder="Club Coins" />
          </div>
          <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer">
            <input type="checkbox" checked={!!form.leaderboard_enabled} onChange={e => setForm(f => ({...f, leaderboard_enabled: e.target.checked}))} />
            Ranglijst zichtbaar voor alle leden
          </label>

          <div style="font-size:12px;font-weight:600;color:var(--color-text-muted);margin-top:4px;text-transform:uppercase;letter-spacing:.04em">Coins & goedkeuring per actie</div>
          <div style="display:flex;flex-direction:column;gap:6px">
            {ACTION_TYPES.map(at => (
              <div key={at.key} style="display:grid;grid-template-columns:1fr 80px auto;gap:8px;align-items:center;padding:8px 10px;border-radius:8px;border:1px solid var(--color-border);background:var(--color-bg-secondary)">
                <span style="font-size:12px">{at.label}</span>
                <input class="form-input" style="font-size:12px;text-align:center;padding:4px 6px" type="number" min="0"
                  value={form[at.coinsKey] ?? 0}
                  onInput={e => setForm(f => ({...f, [at.coinsKey]: parseInt(e.target.value) || 0}))} />
                {at.autoKey ? (
                  <label style="display:flex;align-items:center;gap:4px;font-size:11px;white-space:nowrap;cursor:pointer">
                    <input type="checkbox" checked={!!form[at.autoKey]}
                      onChange={e => setForm(f => ({...f, [at.autoKey]: e.target.checked}))} />
                    Auto
                  </label>
                ) : (
                  <span style="font-size:11px;color:var(--color-text-muted);white-space:nowrap">{at.approval ? 'Altijd goedkeuren' : 'Altijd auto'}</span>
                )}
              </div>
            ))}
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
            <div>
              <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Seizoensreset maand</label>
              <input class="form-input" style="font-size:13px" type="number" min="1" max="12"
                value={form.season_reset_month ?? 12}
                onInput={e => setForm(f => ({...f, season_reset_month: parseInt(e.target.value) || 12}))} />
            </div>
            <div>
              <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Seizoensreset dag</label>
              <input class="form-input" style="font-size:13px" type="number" min="1" max="31"
                value={form.season_reset_day ?? 24}
                onInput={e => setForm(f => ({...f, season_reset_day: parseInt(e.target.value) || 24}))} />
            </div>
          </div>

          <div>
            <Button variant="primary" size="sm" onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? '…' : 'Instellingen opslaan'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Rewards management */}
      <Card title="🎁 Beloningen beheren">
        {rewards.length > 0 && (
          <div style="display:flex;flex-direction:column;gap:6px;margin-bottom:12px">
            {rewards.map(r => (
              <div key={r.id} style="display:flex;align-items:center;gap:10px;padding:8px 12px;background:var(--color-bg-secondary);border-radius:8px;border:1px solid var(--color-border)">
                {r.image_url && (
                  <img src={r.image_url} alt={r.name} style="width:40px;height:40px;object-fit:cover;border-radius:6px;flex-shrink:0" />
                )}
                <div style="flex:1;min-width:0">
                  <div style="font-size:13px;font-weight:500">{r.name} <span style="color:var(--color-primary)">🪙 {r.cost_coins}</span></div>
                  {r.description && <div style="font-size:11px;color:var(--color-text-muted)">{r.description}</div>}
                  <div style="font-size:11px;color:var(--color-text-muted)">{r.stock !== null ? `Voorraad: ${r.stock}` : 'Onbeperkt'} · {r.is_active ? '✅ Actief' : '⏸ Inactief'}</div>
                </div>
                <div style="display:flex;gap:4px;flex-shrink:0">
                  <button onClick={() => setRewardForm({...r})} style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 7px;cursor:pointer;font-size:12px">✏️</button>
                  <button onClick={() => handleDeleteReward(r.id)} style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 7px;cursor:pointer;font-size:12px">🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {rewardForm ? (
          <div style="padding:12px;border:1px solid var(--color-border);border-radius:8px;background:var(--color-bg-secondary);display:flex;flex-direction:column;gap:8px">
            <input class="form-input" style="font-size:13px" placeholder="Naam beloning *" value={rewardForm.name || ''} onInput={e => setRF('name', e.target.value)} />
            <textarea class="form-textarea" style="font-size:13px;min-height:50px" placeholder="Omschrijving (optioneel)" value={rewardForm.description || ''} onInput={e => setRF('description', e.target.value)} />
            <AiAssist kind="reward_description" value={rewardForm.description || ''} context={{ title: rewardForm.name || '' }} onUse={text => setRF('description', text)} />
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
              <div>
                <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Kostprijs (coins) *</label>
                <input class="form-input" style="font-size:13px" type="number" min="1" value={rewardForm.cost_coins || ''} onInput={e => setRF('cost_coins', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Voorraad (leeg = onbeperkt)</label>
                <input class="form-input" style="font-size:13px" type="number" min="0" value={rewardForm.stock ?? ''} onInput={e => setRF('stock', e.target.value === '' ? null : parseInt(e.target.value))} />
              </div>
            </div>
            {rewardForm.id && (
              <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer">
                <input type="checkbox" checked={!!rewardForm.is_active} onChange={e => setRF('is_active', e.target.checked)} />
                Actief (zichtbaar in de winkel)
              </label>
            )}
            {rewardForm.id && (
              <div>
                <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:4px">Foto</label>
                {rewardForm.image_url && (
                  <img src={rewardForm.image_url} alt="preview" style="width:120px;height:120px;object-fit:cover;border-radius:6px;margin-bottom:6px;display:block" />
                )}
                <label style={`display:inline-block;padding:5px 10px;border:1px solid var(--color-border);border-radius:6px;font-size:12px;cursor:${uploadingImage ? 'wait' : 'pointer'}`}>
                  {uploadingImage ? 'Uploaden…' : rewardForm.image_url ? '📷 Foto wijzigen' : '📷 Foto toevoegen'}
                  <input type="file" accept="image/*" style="display:none" disabled={uploadingImage}
                    onChange={e => e.target.files?.[0] && handleUploadImage(e.target.files[0])} />
                </label>
              </div>
            )}
            {!rewardForm.id && (
              <p style="font-size:11px;color:var(--color-text-muted);margin:0">Sla de beloning eerst op om daarna een foto toe te voegen.</p>
            )}
            {rewardError && <p style="color:var(--color-danger,#c0392b);font-size:13px;margin:4px 0 0">{rewardError}</p>}
            <div style="display:flex;gap:6px">
              <Button variant="primary" size="sm" onClick={handleSaveReward} disabled={savingReward || !rewardForm.name?.trim() || !rewardForm.cost_coins}>
                {savingReward ? '…' : (rewardForm.id ? 'Opslaan' : 'Toevoegen')}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => { setRewardForm(null); setRewardError(null); }}>Annuleren</Button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setRewardForm({ name: '', description: '', cost_coins: '', stock: null, is_active: true })}
            style="background:none;border:1px dashed var(--color-border);border-radius:8px;width:100%;padding:8px;color:var(--color-text-muted);font-size:13px;cursor:pointer"
          >
            + Beloning toevoegen
          </button>
        )}
      </Card>

      {/* Pending redemptions */}
      <Card title={`📬 Inwisselverzoeken${pendingRedemptions.length > 0 ? ` (${pendingRedemptions.length} openstaand)` : ''}`}>
        {redemptions.length === 0 ? (
          <p class="text-muted text-sm">Nog geen inwisselverzoeken.</p>
        ) : (
          <div style="display:flex;flex-direction:column;gap:6px">
            {redemptions.map(r => (
              <div key={r.id} style={`display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:8px;border:1px solid var(--color-border);background:${r.status === 'pending' ? 'var(--color-bg-secondary)' : 'transparent'};opacity:${r.status !== 'pending' ? 0.6 : 1}`}>
                <div style="flex:1;min-width:0">
                  <div style="font-size:13px;font-weight:500">{r.user_name} → {r.reward_name}</div>
                  <div style="font-size:11px;color:var(--color-text-muted)">🪙 {r.cost_coins} · {timeAgo(r.created_at)}</div>
                </div>
                {r.status === 'pending' ? (
                  <div style="display:flex;gap:4px;flex-shrink:0">
                    <button onClick={() => handleRedemption(r.id, 'approved')} disabled={handling === r.id + ':redemption'}
                      style="background:#16a34a;color:#fff;border:none;border-radius:6px;padding:4px 10px;cursor:pointer;font-size:12px;font-weight:500">
                      ✓ Goedkeuren
                    </button>
                    <button onClick={() => handleRedemption(r.id, 'rejected')} disabled={handling === r.id + ':redemption'}
                      style="background:#dc2626;color:#fff;border:none;border-radius:6px;padding:4px 10px;cursor:pointer;font-size:12px;font-weight:500">
                      ✗ Afwijzen
                    </button>
                  </div>
                ) : (
                  <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${r.status === 'approved' ? '#dcfce7' : '#fee2e2'};color:${r.status === 'approved' ? '#16a34a' : '#dc2626'}`}>
                    {r.status === 'approved' ? 'Goedgekeurd' : 'Afgewezen'}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export default function CoinsView() {
  const auth = useAuth();
  const isAdmin = auth.isPlatformAdmin || auth.hasPermission('settings', 'update');
  const [activeTab, setActiveTab] = useState('balance');
  const [currencyName, setCurrencyName] = useState('Club Coins');

  useEffect(() => {
    api('/api/coins/settings')
      .then(s => setCurrencyName(s.currency_name || 'Club Coins'))
      .catch(() => {});
  }, []);

  const tabs = [
    { id: 'balance',     label: `🪙 ${currencyName}` },
    { id: 'leaderboard', label: '🏆 Ranglijst' },
    { id: 'shop',        label: '🎁 Beloningswinkel' },
    ...(isAdmin ? [{ id: 'admin', label: '⚙️ Beheer' }] : []),
  ];

  const tabStyle = (id) => ({
    padding: '8px 14px',
    background: 'none',
    border: 'none',
    borderBottom: activeTab === id ? '2px solid var(--color-primary)' : '2px solid transparent',
    color: activeTab === id ? 'var(--color-primary)' : 'var(--color-text-muted)',
    fontWeight: activeTab === id ? '600' : '400',
    fontSize: '13px',
    cursor: 'pointer',
    marginBottom: '-1px',
    whiteSpace: 'nowrap',
  });

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">🪙 {currencyName}</h1>
      </div>
      <div style="display:flex;border-bottom:1px solid var(--color-border);margin-bottom:20px;overflow-x:auto">
        {tabs.map(tab => (
          <button key={tab.id} style={tabStyle(tab.id)} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'balance'     && <MyBalanceTab currencyName={currencyName} />}
      {activeTab === 'leaderboard' && <LeaderboardTab currencyName={currencyName} />}
      {activeTab === 'shop'        && <RewardsTab currencyName={currencyName} />}
      {activeTab === 'admin'       && isAdmin && <AdminTab />}
    </div>
  );
}
